import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import AttivaCard from "../../../components/card/AttivaCard";
import Dropzone from "../../../components/documents/Dropzone";
import RecentDocuments from "../../../components/documents/RecentDocuments";
import { Loader } from "../../../components/loader/SpinnerLogo";
import useFetch from "../../../hooks/useFetch";
import PageHeader from "../../../layout/shared/page-header";
import { menuRoutes } from "../../../router/menu";
import { ProductService } from "../../../services/product/productService";
import { Product } from "../../../type/entities/product-type";
import { formatValidationError, PRODUCT_FIELD_LABELS } from "../../../utils/validationErrorLabels";
import ProductForm from "../ProductForm";
import Swal from "sweetalert2";

export default function ProductEdit() {

    const { id = "" } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const service = new ProductService();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [updateDocumentsList, setUpdateDocumentsList] = useState<boolean>(false);

    const [data, loading] = useFetch(useCallback(async () => {
        const response = await service.getProductById(id as string);
        return response.getResponseData() as Product;
    }, [id]));

    const pageHeader = {
        title: `Editar Producto - ${data?.name}`,
        breadcrumb: [
            { name: 'Administración' },
            { name: 'Gestión de productos' },
            { href: menuRoutes.products.path, name: "Productos" },
            { name: "Editar" },
        ],
    };

    const handleSubmit = async (values: any) => {

        // En el caso de cambiar el tipo de producto a 'CursoBonificado', se eliminarán todas las credenciales de inspector asociadas al producto, mostrar un mensaje con swal de confirmación.
        if(values.productTypeId != '26a41eb6-a9d1-4d2f-88e5-1bb59bdb9ae1' && data?.productType?.id === '26a41eb6-a9d1-4d2f-88e5-1bb59bdb9ae1') {
            const result = await Swal.fire({
                title: '¿Estás seguro de querer cambiar el tipo de producto?',
                text: 'Esta acción eliminará todas las credenciales de inspector asociadas al producto.',
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: 'Eliminar',
                cancelButtonText: 'Cancelar',
                allowOutsideClick: false,
            });
            if(!result.isConfirmed) {
                return;
            }
        }

        setIsLoading(true);
        try {
            const {
                contractWatermarkFile,
                removeContractWatermark,
                ...payload
            } = values;

            const response = await (
                await service.editProduct(payload)
            ).getResponseData();
            if (response.success) {
                if (contractWatermarkFile || removeContractWatermark) {
                    const uploadResponse = await (
                        await service.updateContractWatermark(id, contractWatermarkFile, removeContractWatermark)
                    ).getResponseData();

                    if (!uploadResponse.success) {
                        toast.error(uploadResponse.message ?? 'El producto se ha actualizado, pero no se ha podido guardar la marca de agua');
                    }
                }

                navigate(-1);
                toast.success("Producto editado correctamente");
            } else {
                response.data.errors?.forEach((error: any) => {
                    toast.error(formatValidationError(error, PRODUCT_FIELD_LABELS));
                });
            }
        } catch (error: any) {
            toast.error(error.message);
        } finally {
            setIsLoading(false);
        }
    };

    if (loading) return <Loader />;

    return (
        <>
            <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb} />

            <AttivaCard title="Datos Principales" className="mb-4">
                {(data !== null && data !== undefined) && <ProductForm data={data} submit={handleSubmit} isLoading={isLoading} />}
            </AttivaCard>

            <AttivaCard title="Fichas del producto" className="mb-4">
                <div className="mb-4 rounded-md border border-indigo-200 bg-indigo-50 px-4 py-3">
                    <p className="text-sm font-semibold text-indigo-900">Documentación de fichas</p>
                    <p className="mt-1 text-sm text-indigo-800">
                        En esta sección debes adjuntar las fichas del producto (una o varias) en formato PDF. Estas fichas se utilizarán en el proceso de firma y en las comunicaciones automáticas del contrato.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="md:col-span-3 col-span-1">
                        <Dropzone
                            id={id}
                            entity='product'
                            setUpdateList={setUpdateDocumentsList}
                            acceptedMimeTypes={{ 'application/pdf': ['.pdf'] }}
                            invalidFilesMessage='Las fichas de producto solo admiten archivos PDF.'
                        />
                    </div>
                    <div className="md:col-span-1 col-span-1">
                        <RecentDocuments
                            updateList={updateDocumentsList}
                            setUpdateList={setUpdateDocumentsList}
                            service={() => (new ProductService()).getProductDocuments(id, 10)}
                        />
                    </div>
                </div>
            </AttivaCard>
        </>
    );
}
